import React, { useState, useEffect } from "react";
import axios from "axios";
import { Table } from "../../../components/ui/table";
import { Button } from "../../../components/ui/button";
import Navbar from '../../../components/Header/Header';
import Footer from "../../../components/Footer/Footer";
import MasterAccessActionBar from "./MasterAccessActionBar";

const MasterAccess = () => {
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);

  const fetchUsers = async () => {
    console.log('=== fetchUsers called ===');
    try {
      console.log('Making API request to /admin/master-access');
      const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/admin/master-access`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "Content-Type": "application/json",
        },
      });
      console.log('API response:', response.data);
      setUsers(response.data);
      setFilteredUsers(response.data);
      setError(null);
    } catch (error) {
      console.error("Error fetching users:", error);
      console.error("Error details:", error.response?.data || error.message);
      setError("Failed to fetch users");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    console.log('=== MasterAccess component mounted ===');
    fetchUsers();
  }, []);

  const handleSearch = (query) => {
    console.log('=== handleSearch called with query:', query);
    if (!query) {
      setFilteredUsers(users);
      return;
    }
    const filtered = users.filter((user) =>
      user.Username.toLowerCase().includes(query.toLowerCase())
    );
    console.log('Filtered users:', filtered);
    setFilteredUsers(filtered);
  };

  const handleEdit = (user) => {
    console.log('=== handleEdit called for user:', user);
    setSelectedUser(user);
  };

  const handleAccessChange = async (userId, field, value) => {
    try {
      console.log('Updating access:', { userId, field, value });
      const response = await axios.put(
        `${process.env.REACT_APP_API_BASE_URL}/admin/master-access/${userId}`,
        {
          LocationAccess: field === 'LocationAccess' ? (value ? 1 : 0) : users.find(u => u.UserID === userId)?.LocationAccess ? 1 : 0,
          ProjectAccess: field === 'ProjectAccess' ? (value ? 1 : 0) : users.find(u => u.UserID === userId)?.ProjectAccess ? 1 : 0,
          CategoryAccess: field === 'CategoryAccess' ? (value ? 1 : 0) : users.find(u => u.UserID === userId)?.CategoryAccess ? 1 : 0,
          UnitAccess: field === 'UnitAccess' ? (value ? 1 : 0) : users.find(u => u.UserID === userId)?.UnitAccess ? 1 : 0,
          ItemsAccess: field === 'ItemsAccess' ? (value ? 1 : 0) : users.find(u => u.UserID === userId)?.ItemsAccess ? 1 : 0
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            "Content-Type": "application/json",
          },
        }
      );
      console.log('Access update response:', response.data);
      
      if (response.data.message === "Master access updated successfully") {
        // Fetch fresh data after successful update
        await fetchUsers();
      }
    } catch (error) {
      console.error('Error updating access:', error);
      setError("Failed to update access");
    }
  };

  const columns = [
    {
      header: "Username",
      accessorKey: "Username",
    },
    {
      header: "Location Access",
      accessorKey: "LocationAccess",
      cell: ({ row }) => (
        <div className="flex items-center">
          <input
            type="checkbox"
            checked={row.original.LocationAccess || false}
            onChange={(e) => handleAccessChange(row.original.UserID, 'LocationAccess', e.target.checked)}
            className="h-4 w-4"
          />
        </div>
      ),
    },
    {
      header: "Project Access",
      accessorKey: "ProjectAccess",
      cell: ({ row }) => (
        <div className="flex items-center">
          <input
            type="checkbox"
            checked={row.original.ProjectAccess || false}
            onChange={(e) => handleAccessChange(row.original.UserID, 'ProjectAccess', e.target.checked)}
            className="h-4 w-4"
          />
        </div>
      ),
    },
    {
      header: "Category Access",
      accessorKey: "CategoryAccess",
      cell: ({ row }) => (
        <div className="flex items-center">
          <input
            type="checkbox"
            checked={row.original.CategoryAccess || false}
            onChange={(e) => handleAccessChange(row.original.UserID, 'CategoryAccess', e.target.checked)}
            className="h-4 w-4"
          />
        </div>
      ),
    },
    {
      header: "Unit Access",
      accessorKey: "UnitAccess",
      cell: ({ row }) => (
        <div className="flex items-center">
          <input
            type="checkbox"
            checked={row.original.UnitAccess || false}
            onChange={(e) => handleAccessChange(row.original.UserID, 'UnitAccess', e.target.checked)}
            className="h-4 w-4"
          />
        </div>
      ),
    },
    {
      header: "Items Access",
      accessorKey: "ItemsAccess",
      cell: ({ row }) => (
        <div className="flex items-center">
          <input
            type="checkbox"
            checked={row.original.ItemsAccess || false}
            onChange={(e) => handleAccessChange(row.original.UserID, 'ItemsAccess', e.target.checked)}
            className="h-4 w-4"
          />
        </div>
      ),
    },
    {
      header: "Actions",
      cell: ({ row }) => (
        <Button
          variant="outline"
          size="sm"
          onClick={() => handleEdit(row.original)}
        >
          Edit
        </Button>
      ),
    },
  ];

  if (isLoading) {
    return (
      <div className="min-h-screen flex flex-col">
        <Navbar />
        <div className="flex-grow flex items-center justify-center">
          <div>Loading...</div>
        </div>
        <Footer />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex flex-col">
        <Navbar />
        <div className="flex-grow flex items-center justify-center">
          <div className="text-red-500">{error}</div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <div className="flex-grow container mx-auto p-4">
        <h1 className="text-2xl font-bold mb-4">Master Access Management</h1>
        <MasterAccessActionBar 
          onRefresh={fetchUsers} 
          onSearch={handleSearch} 
          selectedUser={selectedUser}
          setSelectedUser={setSelectedUser}
        />
        <Table columns={columns} data={filteredUsers} />
      </div>
      <Footer />
    </div>
  );
};

export default MasterAccess;